import dayjs from 'dayjs';
import { compact } from 'lodash';

import {
  CreatedWithEnum,
  CustomUser,
  Sankey,
  StatementTypeEnum,
} from 'generated-api';

import { toDecimalString } from './number';

export const emptySankey: Sankey = {
  id: 0,
  owner_id: 0,
  company_name: 'My Company',
  fiscal_year: dayjs().year(),
  reporting_period: 'full_year',
  statement_type: 'other',
  use_custom_title: false,
  custom_title: 'Diagram Title',
  created_with: 'excel',
  data_origin: 'excel',
  data_source_notes: '',
  node_width: 30,
  node_height: 22,
  node_spacing: 51,
  font_size: 12,
  unit_prefix: '$',
  unit_suffix: 'B',
  canvas_size: '3',
  canvas_width: 960,
  canvas_height: 600,
  show_title: true,
  title_font_size: 24,
  title_font_color: '#366B87',
  show_imbalances: true,
  show_labels: true,
  show_comparison_line: true,
  label_pos: 'outside',
  nr_of_decimals: '1',
  download_scale_png: '2',
  js_input: '',
  show_watermark: false,
  font_family: 'Inter',
  background_color: 'transparent',
};

export function convertToMaticInput(
  flows: SankeyFormFlow[],
  nodes: SankeyFormNode[]
) {
  let maticFlows = '';
  let maticNodes = '';

  for (const flow of flows) {
    if (flow.source && flow.target) {
      // Flow description format: "source [amount][amountComparison] target"
      maticFlows +=
        flow.source +
        ' [' +
        toDecimalString(flow.amount) +
        '][' +
        toDecimalString(flow.amountComparison) +
        '] ' +
        flow.target +
        '\n';
    }
  }
  for (const node of nodes) {
    if (node.name) {
      // Node description format: ":name color [use_custom_amount_label] custom_amount_label [use_custom_comparison_label] custom_comparison_label"
      maticNodes +=
        ':' +
        node.name +
        ' ' +
        node.color +
        ' [' +
        node.use_custom_amount_label +
        '] ' +
        node.custom_amount_label +
        ' [' +
        node.use_custom_comparison_label +
        '] ' +
        node.custom_comparison_label +
        '\n';
    }
  }
  return maticFlows + maticNodes;
}

/*
 * Converts a map ID -> node into a flat array of nodes that is easy to iterate through.
 */
export const sankeyFormNodesMapToArray = (
  nodes: Record<string, SankeyFormNode | undefined> | undefined
): SankeyFormNode[] => compact(Object.values(nodes ?? {}));

/*
 * Converts an array of nodes into a map ID -> node that SankeyForm needs.
 */
export const sankeyFormNodesArrayToMap = (
  nodes: SankeyFormNode[]
): Record<string, SankeyFormNode> =>
  Object.fromEntries(nodes.map((node) => [node.id, node]));

/*
 * Converts a map ID -> flow into a flat array of flows that is easy to iterate through.
 */
export const sankeyFormFlowsMapToArray = (
  flows: Record<string, SankeyFormFlow> | undefined
): SankeyFormFlow[] => {
  return Object.values(flows ?? {}).sort(
    (a, b) => (a.dataGridRowIndex ?? 0) - (b.dataGridRowIndex ?? 0)
  );
};

/*
 * Converts an array of flows into a map ID -> flow that SankeyForm needs.
 */
export const sankeyFormFlowsArrayToMap = (
  flows: SankeyFormFlow[]
): Record<string, SankeyFormFlow> =>
  Object.fromEntries(flows.map((flow) => [flow.id, flow]));

// TODO: Unit test this method.
// TODO: Unify this method with Django create_new view.
export const buildTemplateCopy = (
  templateSankey: Sankey,
  user: CustomUser | undefined,
  created_with: CreatedWithEnum
): WithExplicitFields<Sankey> => ({
  /* Copied values */
  background_color: templateSankey.background_color,
  canvas_height: templateSankey.canvas_height,
  canvas_size: templateSankey.canvas_size,
  canvas_width: templateSankey.canvas_width,
  chart: templateSankey.chart,
  company_name: templateSankey.company_name,
  comparison_line_prefix: templateSankey.comparison_line_prefix,
  comparison_line_suffix: templateSankey.comparison_line_suffix,
  comparison_line_value_type: templateSankey.comparison_line_value_type,
  custom_title: templateSankey.custom_title,
  data_source_notes: templateSankey.data_source_notes,
  download_image_format: templateSankey.download_image_format,
  download_scale_png: templateSankey.download_scale_png,
  fiscal_year: templateSankey.fiscal_year,
  font_family: templateSankey.font_family,
  font_size: templateSankey.font_size,
  js_input: templateSankey.js_input,
  label_pos: templateSankey.label_pos,
  node_height: templateSankey.node_height,
  node_spacing: templateSankey.node_spacing,
  node_width: templateSankey.node_width,
  nr_of_decimals: templateSankey.nr_of_decimals,
  remembered_node_moves: templateSankey.remembered_node_moves,
  reporting_period: templateSankey.reporting_period,
  second_label_line_value_type: templateSankey.second_label_line_value_type,
  show_canvas_resolution: templateSankey.show_canvas_resolution,
  show_comparison_line: templateSankey.show_comparison_line,
  show_data_editor: templateSankey.show_data_editor,
  show_imbalances: templateSankey.show_imbalances,
  show_labels: templateSankey.show_labels,
  show_title: templateSankey.show_title,
  statement_type: templateSankey.statement_type,
  title_font_color: templateSankey.title_font_color,
  title_font_size: templateSankey.title_font_size,
  unit_prefix: templateSankey.unit_prefix,
  unit_suffix: templateSankey.unit_suffix,
  use_custom_title: templateSankey.use_custom_title,
  /* Overrides */
  chart_type_list_position: undefined,
  chart_type_name: undefined,
  created_at: undefined,
  created_with,
  data_origin: 'inline',
  id: 0,
  is_listed_chart_type_in_footer: false,
  is_main_example: false,
  is_official: false,
  is_template: false,
  owner_id: user?.id ?? 0,
  points: undefined,
  public_permission: 'no-access',
  show_watermark: false,
  svg_representation: undefined,
  team_permission: 'no-access',
});

export const buildExcelSankey = (): WithExplicitFields<Sankey> => ({
  background_color: 'transparent',
  canvas_height: 600,
  canvas_size: '3',
  canvas_width: 960,
  chart_type_list_position: 1,
  chart_type_name: '',
  chart: undefined,
  company_name: 'My Company',
  comparison_line_prefix: '',
  comparison_line_suffix: '',
  comparison_line_value_type: 'percent',
  created_at: dayjs().toISOString(),
  created_with: 'excel',
  custom_title: 'Diagram Title',
  data_origin: 'excel',
  data_source_notes: '',
  download_image_format: 'png',
  download_scale_png: '2',
  fiscal_year: dayjs().year(),
  font_family: 'Inter',
  font_size: 12,
  id: 0,
  is_listed_chart_type_in_footer: false,
  is_main_example: false,
  is_official: false,
  is_template: false,
  js_input: '',
  label_pos: 'outside',
  node_height: 22,
  node_spacing: 51,
  node_width: 30,
  nr_of_decimals: '1',
  owner_id: 0,
  points: 1,
  public_permission: 'no-access',
  remembered_node_moves: '',
  reporting_period: 'full_year',
  second_label_line_value_type: 'amount',
  show_canvas_resolution: false,
  show_comparison_line: true,
  show_data_editor: false,
  show_imbalances: true,
  show_labels: true,
  show_title: true,
  show_watermark: false,
  statement_type: 'other',
  svg_representation: undefined,
  team_permission: 'no-access',
  title_font_color: '#366B87',
  title_font_size: 24,
  unit_prefix: '$',
  unit_suffix: 'B',
  use_custom_title: false,
});

export const isFinancialSankey = (sankey: Sankey) =>
  sankey.statement_type === StatementTypeEnum.BALANCE ||
  sankey.statement_type === StatementTypeEnum.CASHFLOW ||
  sankey.statement_type === StatementTypeEnum.INCOME;

// Protected diagrams won't be deletable from the UI (only through Django admin).
export const isProtectedSankey = (sankey: Sankey) =>
  sankey.is_main_example || sankey.is_template;
